import {
    AudioFolderData,
    AudioTrack,
    AudioTrackData,
    ChangedDataKeys,
    ICaptionSizeState,
    IChangedData,
    ILang,
    ISpace,
    ISubtitlesDataAll,
    LocalStorageKeys,
} from "./types";
import { convertTime } from "../../utils";
import { formatCueTime } from "./editorErrors";
import { voicingSounds } from "./dubSounding";

export function timeStringToMs(timeString: string) {
    // Split the time string by colon and period
    const parts = timeString.split(":");
    const secondsParts = parts[2].split(".");

    // Extract hours, minutes, seconds, and milliseconds
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(secondsParts[0], 10);
    const milliseconds = parseInt(secondsParts[1], 10);

    // Calculate total milliseconds
    return hours * 3600000 + minutes * 60000 + seconds * 1000 + milliseconds;
}

export function tryParse<T>(string: string) {
    try {
        return JSON.parse(string) as T;
    } catch (error) {
        console.log("wrong format");
        return false;
    }
}

// NOTE: the dub is assembled by SEQUENTIAL concatenation (concat_folder_synth_files):
// each voicing plays fully, then the next begins — two voices never play at once, so
// there is never an audible overlap. A voicing running past the next cue's start only
// means the translation is denser than the source timing (it drifts later), not a
// collision. We therefore no longer surface the red "Beginning of fragment outside"
// marking (trimmed_start is left null below). getTrimmed is kept for length/timeline use.
const getTrimmed = (oneTrack: AudioTrackData, nextTrackStart: number | null) => {
    return nextTrackStart && nextTrackStart < oneTrack.start + oneTrack.length
        ? oneTrack.start + oneTrack.length - nextTrackStart
        : null;
};

export const getNextStart = (id: string, dataAll: AudioFolderData[], folder: number) => {
    // Звучащие озвучки: снятая с озвучки (⊘) молчит, хоть её старый `file` и на записи.
    const translations = dataAll[folder]
        ? dataAll[folder].translations.filter(voicingSounds)
        : [];
    const currentIndex = translations.findIndex((one) => one["id"] === id);
    if (currentIndex < translations.length - 1) {
        return translations[currentIndex + 1].start;
    }
    // Last voiced cue of this folder: overlap can still cross INTO the next folder.
    // In this folder's local timeline the next folder begins at folderDuration
    // (= gap between the two folders' prev_mp3_length_aggr), and its first voicing
    // sits at folderDuration + firstNextTrack.start. Comparing the current cue's end
    // to that catches cross-folder overlaps.
    const currentFolder = dataAll[folder];
    const nextFolder = dataAll[folder + 1];
    if (currentFolder && nextFolder) {
        const nextTracks = nextFolder.translations.filter(voicingSounds);
        if (nextTracks.length) {
            const folderDuration =
                parseInt(nextFolder.prev_mp3_length_aggr) -
                parseInt(currentFolder.prev_mp3_length_aggr);
            return folderDuration + nextTracks[0].start;
        }
    }
    // Truly last voiced cue of the lecture — nothing following to overlap.
    return translations[currentIndex].start + translations[currentIndex].length;
};

export const getPrevTrackId = (currentTrackId: string, tracks: AudioTrack[]) => {
    const currentIndex = tracks.findIndex((track) => track.id === currentTrackId);
    return currentIndex === 0 || currentIndex === -1
        ? tracks[0]["id"]
        : tracks[currentIndex - 1].id;
};

export const prepareDataOne = ({
    one,
    folder_num,
    allData,
    transCaption,
}: {
    one: AudioTrackData;
    folder_num: number;
    allData: AudioFolderData[];
    transCaption?: Pick<AudioTrackData, "voice" | "captions" | "speed" | "translationStart">;
}) => {
    const folder = folder_num - 1;
    let length;
    const end_rus_milli = calcEndRusMilli(one);
    const nextTrackStart = voicingSounds(one) && getNextStart(one["id"], allData, folder);
    if (nextTrackStart && nextTrackStart < one["start"] + one["length"]) {
        // length = nextTrackStart - one["start"];
    }
    const trimmed = nextTrackStart && getTrimmed(one, nextTrackStart);
    let newDataOne = {
        ...one,
        original_length: one["length"],
        length: length ? length : one["length"],
        trimmed: trimmed ? trimmed : null,
        // No audible overlap exists in the concatenated dub → never mark "fragment outside".
        trimmed_start: null,
        original_start: one["start"],
        original_shifted_start: one["shifted_start"],
        start: one.start,

        start_rus: formatMilliseconds(one.start),
        end_rus: formatMilliseconds(one.start + one.length),
        start_rus_milli: one.start_rus_milli,
        end_rus_milli: end_rus_milli,
        original_id: one["id"],
        id: `${one["folder_num"]}_${one["id"]}`,
        translation: transCaption?.captions,
    };
    if (transCaption) {
        newDataOne.translation = transCaption.captions;
        newDataOne.voice = transCaption.voice;
        newDataOne.speed = transCaption.speed;
        newDataOne.translationStart = transCaption.translationStart;
    }

    return newDataOne;
};

// --- Куски одной фразы (раскладка дубляжа по фразам, split_voicing) ------------------------
// У каждого куска своя часть перевода и своя озвучка, а русский текст — вся фраза родителя
// (split_parent). Колонка оригинала показывала эту фразу 2–5 раз подряд, и 01.10.2026
// переводчица лекции 4119e839 (ru_es, папка 21) удалила 10 кусков, приняв их за дубли.
// Настоящие дубли — близнецы R10 (реплика с текстом следующей, без split_parent) — сюда не
// попадают: их редактор показывает как есть, их и надо видеть и удалять.
//
// Но не всякая группа кусков — законная раскладка: у прежних прогонов в КАЖДЫЙ кусок попадал
// весь перевод фразы (043f05ea, ru_en, папка 24: четыре куска с «Check it for yourself…» — фраза
// звучит в дубляже раз за разом). Такой кусок — настоящий повтор, и вопросы редактора говорят
// это, а не «не дубль, своя часть перевода». Правило то же, что на сервере: переводы кусков
// схожи ≥ 0,85 (законная раскладка — 0,26…0,61, сломанная — 0,96…1,00).

/** Текст для сравнения кусков: без регистра, «ё» как «е», без знаков препинания. */
const pieceText = (captions: string | null | undefined): string =>
    (captions || "")
        .toLowerCase()
        .replace(/ё/g, "е")
        // Конструктор, а не литерал: флаг u у литерала tsc при target es5 не принимает.
        .replace(new RegExp("[^\\p{L}\\p{N}]+", "gu"), " ")
        .trim();

/** Порог схожести переводов кусков, с которого кусок — повтор соседнего, а не своя часть. */
export const DUB_REPEAT_SIMILARITY = 0.85;

/** Переводы кусков — фразы, а не страницы; предел держит цену сравнения O(n·m) малой. */
const SIMILARITY_MAX_CHARS = 1000;

// Отрезки — объекты, а не кортежи: парсер eslint сборки (старый typescript-estree) кортежных
// типов нынешнего TypeScript не читает и роняет сборку.
/** Участок сравнения: a[alo..ahi) против b[blo..bhi). */
interface CompareSpan {
    alo: number;
    ahi: number;
    blo: number;
    bhi: number;
}

/** Общий отрезок: начало в a, начало в b, длина. */
interface CommonRun {
    i: number;
    j: number;
    size: number;
}

/** Самый длинный общий отрезок a[alo..ahi) и b[blo..bhi). */
const longestCommonRun = (a: string, b: string, { alo, ahi, blo, bhi }: CompareSpan): CommonRun => {
    const width = bhi - blo + 1;
    let prev = new Int32Array(width);
    let cur = new Int32Array(width);
    let best = 0;
    let bestI = alo;
    let bestJ = blo;
    for (let i = alo; i < ahi; i++) {
        for (let j = blo; j < bhi; j++) {
            const col = j - blo + 1;
            if (a[i] === b[j]) {
                const run = prev[col - 1] + 1;
                cur[col] = run;
                // Строго больше: из равных берётся самый ранний — как у difflib.
                if (run > best) {
                    best = run;
                    bestI = i - run + 1;
                    bestJ = j - run + 1;
                }
            } else {
                cur[col] = 0;
            }
        }
        const swap = prev;
        prev = cur;
        cur = swap;
    }
    return { i: bestI, j: bestJ, size: best };
};

/**
 * Схожесть двух строк — как difflib.SequenceMatcher(None, a, b).ratio() без autojunk:
 * 2·M / (|a| + |b|), где M — сумма совпавших отрезков (Ратклифф — Обершелп). Этим числом
 * повторы дубляжа мерили на сервере, поэтому и порог 0,85 здесь тот же.
 */
export function textSimilarity(a: string, b: string): number {
    const x = a.slice(0, SIMILARITY_MAX_CHARS);
    const y = b.slice(0, SIMILARITY_MAX_CHARS);
    const total = x.length + y.length;
    if (!total) return 1;
    if (!x.length || !y.length) return 0;
    let matched = 0;
    const pending: CompareSpan[] = [{ alo: 0, ahi: x.length, blo: 0, bhi: y.length }];
    while (pending.length) {
        const span = pending.pop() as CompareSpan;
        const { i, j, size } = longestCommonRun(x, y, span);
        if (!size) continue;
        matched += size;
        if (span.alo < i && span.blo < j) pending.push({ alo: span.alo, ahi: i, blo: span.blo, bhi: j });
        if (i + size < span.ahi && j + size < span.bhi) {
            pending.push({ alo: i + size, ahi: span.ahi, blo: j + size, bhi: span.bhi });
        }
    }
    return (2 * matched) / total;
}

/** Схожи ли строки не меньше порога; сначала граница по длинам (как real_quick_ratio). */
const similarAtLeast = (a: string, b: string, threshold: number): boolean => {
    if (!a || !b) return false;
    if ((2 * Math.min(a.length, b.length)) / (a.length + b.length) < threshold) return false;
    return textSimilarity(a, b) >= threshold;
};

/**
 * Повторяет ли перевод куска (по месту `position` в группе) переводы других кусков фразы.
 * Номера (с 1) кусков, чей перевод схож с ним ≥ 0,85; если таких нет — все остальные куски,
 * когда он схож с их переводами ВМЕСТЕ (в куске весь перевод фразы, у остальных — её части).
 * null — у куска своя часть перевода (или перевода нет).
 */
const dubRepeatOf = (translations: string[], position: number): { of: number[]; whole: boolean } | null => {
    const own = translations[position];
    if (!own) return null;
    const of: number[] = [];
    translations.forEach((other, k) => {
        if (k !== position && similarAtLeast(own, other, DUB_REPEAT_SIMILARITY)) of.push(k + 1);
    });
    if (of.length) return { of, whole: false };
    const others: number[] = [];
    translations.forEach((other, k) => {
        if (k !== position && other) others.push(k);
    });
    // С одним соседом «вместе» — то же, что по одному, и уже проверено.
    if (others.length < 2) return null;
    const joined = others.map((k) => translations[k]).join(" ");
    return similarAtLeast(own, joined, DUB_REPEAT_SIMILARITY) ? { of: others.map((k) => k + 1), whole: true } : null;
};

interface PhrasePieceRow {
    start: number;
    captions?: string | null;
    translation?: string;
    folder_num?: string | number;
    split_parent?: string | null;
    piece_index?: number;
    piece_count?: number;
    piece_repeats_text?: boolean;
    piece_shares_text?: boolean;
    piece_dub_repeats?: number[];
    piece_dub_repeats_whole?: boolean;
}

type PhrasePieceMark = Pick<
    PhrasePieceRow,
    | "piece_index"
    | "piece_count"
    | "piece_repeats_text"
    | "piece_shares_text"
    | "piece_dub_repeats"
    | "piece_dub_repeats_whole"
>;

const PIECE_MARK_FIELDS: Array<keyof PhrasePieceMark> = [
    "piece_index",
    "piece_count",
    "piece_repeats_text",
    "piece_shares_text",
    "piece_dub_repeats",
    "piece_dub_repeats_whole",
];

/**
 * Пометить куски фраз: группы по split_parent внутри папки, по времени начала; номер с 1,
 * сколько кусков, повторяет ли русский текст куска текст более раннего куска, делит ли он
 * русский текст с другим куском, и повторяет ли его перевод переводы других кусков (настоящий
 * повтор в дубляже). Группа из одного куска (остальные удалены) не помечается — он выглядит
 * обычной репликой. Строки без split_parent (в том числе близнецы R10) не трогаются и не
 * схлопываются. Порядок и число строк те же, входные объекты не меняются; старые пометки
 * строки, переставшей быть куском (или повтором), снимаются.
 */
export function markPhrasePieces<T extends PhrasePieceRow>(rows: T[]): T[] {
    const groups = new Map<string, number[]>();
    rows.forEach((row, index) => {
        if (!row.split_parent) return;
        const key = `${row.folder_num ?? ""}|${row.split_parent}`;
        const members = groups.get(key);
        if (members) members.push(index);
        else groups.set(key, [index]);
    });
    const marks = new Map<number, PhrasePieceMark>();
    groups.forEach((members) => {
        if (members.length < 2) return;
        const ordered = [...members].sort((a, b) => rows[a].start - rows[b].start || a - b);
        const texts = ordered.map((rowIndex) => pieceText(rows[rowIndex].captions));
        const translations = ordered.map((rowIndex) => pieceText(rows[rowIndex].translation));
        ordered.forEach((rowIndex, position) => {
            const text = texts[position];
            const mark: PhrasePieceMark = {
                piece_index: position + 1,
                piece_count: ordered.length,
                // Повтор — равенство тексту ЛЮБОГО более раннего куска, а не только куска 1:
                // переводчица режет русский куска 1 до его половины (01.10, фраза 234649), а
                // остальные куски по-прежнему несут всю фразу — её показываем один раз.
                piece_repeats_text: text !== "" && texts.slice(0, position).indexOf(text) >= 0,
                piece_shares_text: text !== "" && texts.some((other, k) => k !== position && other === text),
            };
            const repeat = dubRepeatOf(translations, position);
            if (repeat) {
                mark.piece_dub_repeats = repeat.of;
                if (repeat.whole) mark.piece_dub_repeats_whole = true;
            }
            marks.set(rowIndex, mark);
        });
    });
    return rows.map((row, index) => {
        const mark = marks.get(index);
        const hasOldMarks = PIECE_MARK_FIELDS.some((field) => row[field] !== undefined);
        if (!mark && !hasOldMarks) return row;
        const copy = { ...row };
        PIECE_MARK_FIELDS.forEach((field) => {
            delete copy[field];
        });
        return mark ? { ...copy, ...mark } : copy;
    });
}

/** Помеченный кусок фразы, как его называют вопросы перед удалением и склейкой. */
export interface PhrasePieceRef {
    /** Имя реплики на диске (original_id). */
    id: string;
    index: number;
    count: number;
    parent?: string;
    /** Перевод этого куска — его своя часть фразы (или повтор, см. repeatsOf). */
    translation?: string;
    /** Начало реплики в папке, мс. */
    startMs?: number;
    /** Русский текст куска. */
    captions?: string;
    /** Русский текст тот же, что у другого куска этой фразы (обычно — вся фраза у всех). */
    sharesText?: boolean;
    /** Перевод куска повторяет перевод этих кусков (номера с 1): настоящий повтор в дубляже. */
    repeatsOf?: number[];
    /** …повторяет их переводы ВМЕСТЕ: в этом куске весь перевод фразы. */
    repeatsWhole?: boolean;
    /** У законного куска: другие куски той же фразы, чей перевод — настоящий повтор. */
    groupRepeaters?: number[];
}

/** Кусок фразы среди строк оригинала (с пометками markPhrasePieces) или null. */
export function phrasePieceOf(rows: AudioTrack[] | undefined, originalId: string): PhrasePieceRef | null {
    const row = rows?.find((one) => one.original_id === originalId);
    if (!row || !row.piece_index || !row.piece_count || row.piece_count < 2) return null;
    const piece: PhrasePieceRef = {
        id: row.original_id,
        index: row.piece_index,
        count: row.piece_count,
        parent: row.split_parent,
        translation: row.translation,
        startMs: typeof row.start === "number" ? row.start : undefined,
        captions: row.captions,
        sharesText: row.piece_shares_text,
    };
    if (row.piece_dub_repeats?.length) {
        piece.repeatsOf = [...row.piece_dub_repeats];
        if (row.piece_dub_repeats_whole) piece.repeatsWhole = true;
        return piece;
    }
    const repeaters = (rows || [])
        .filter(
            (one) =>
                one !== row &&
                one.split_parent === row.split_parent &&
                String(one.folder_num ?? "") === String(row.folder_num ?? "") &&
                Boolean(one.piece_index) &&
                Boolean(one.piece_dub_repeats?.length),
        )
        .map((one) => one.piece_index as number)
        .sort((a, b) => a - b);
    if (repeaters.length) piece.groupRepeaters = repeaters;
    return piece;
}

const quotePieceTranslation = (piece: PhrasePieceRef, en: boolean): string => {
    const text = (piece.translation || "").trim();
    if (!text) return en ? "(no translation)" : "(перевода нет)";
    return `«${text.length > 300 ? `${text.slice(0, 300)}…` : text}»`;
};

const pieceAt = (piece: PhrasePieceRef): string =>
    typeof piece.startMs === "number" ? ` (${formatCueTime(piece.startMs)})` : "";

const pieceNumbers = (indices: number[], count: number): string => indices.map((i) => `${i}/${count}`).join(", ");

const ofPieces = (indices: number[], count: number, en: boolean): string =>
    en
        ? `${indices.length > 1 ? "pieces" : "piece"} ${pieceNumbers(indices, count)}`
        : `${indices.length > 1 ? "кусков" : "куска"} ${pieceNumbers(indices, count)}`;

/**
 * Как перевод куска повторяет другие: «совпадает с переводом куска 1/2» или «повторяет
 * переводы кусков 2/3, 3/3 вместе — в этом куске весь перевод фразы».
 */
export function phrasePieceRepeatRelation(repeatsOf: number[], whole: boolean, count: number, en: boolean): string {
    if (whole) {
        return en
            ? `repeats the translations of ${ofPieces(repeatsOf, count, en)} together — this piece carries the whole phrase's translation`
            : `повторяет переводы ${ofPieces(repeatsOf, count, en)} вместе — в этом куске весь перевод фразы`;
    }
    return en
        ? `matches the translation of ${ofPieces(repeatsOf, count, en)}`
        : `совпадает с переводом ${ofPieces(repeatsOf, count, en)}`;
}

const isRepeatPiece = (piece: PhrasePieceRef): boolean => Boolean(piece.repeatsOf?.length);

/** Сколько раз фраза звучит в дубляже: кусок весь перевод — дважды, иначе он и его двойники. */
const timesHeard = (piece: PhrasePieceRef): number => (piece.repeatsWhole ? 2 : (piece.repeatsOf?.length ?? 0) + 1);

const timesWord = (times: number, en: boolean): string => {
    if (times === 2) return en ? "twice" : "дважды";
    if (en) return `${times} times`;
    const few = times % 10 >= 2 && times % 10 <= 4 && (times % 100 < 12 || times % 100 > 14);
    return `${times} ${few ? "раза" : "раз"}`;
};

/** Кусок-повтор: что он повторяет, сколько раз звучит фраза и что удаление его — исправление. */
const repeatPieceDeleteQuestion = (piece: PhrasePieceRef, en: boolean): string => {
    const relation = phrasePieceRepeatRelation(piece.repeatsOf || [], Boolean(piece.repeatsWhole), piece.count, en);
    const times = timesHeard(piece);
    if (en) {
        const fix =
            times <= 2
                ? "Deleting this piece is the fix: the phrase will be heard once."
                : "Deleting this piece removes one repeat; delete the other extra pieces the same way, keeping one.";
        return (
            `Cue ${piece.id}${pieceAt(piece)} is piece ${piece.index}/${piece.count} of a phrase, and its translation ` +
            `${relation}: ${quotePieceTranslation(piece, en)}.\n\n` +
            `This is a real repeat in the dub: the phrase is heard ${timesWord(times, en)}. ${fix}\n\nDelete?`
        );
    }
    const fix =
        times <= 2
            ? "Удаление этого куска и есть исправление: фраза прозвучит один раз."
            : "Удаление этого куска уберёт один повтор; остальные лишние куски удалите так же, оставив один.";
    return (
        `Реплика ${piece.id}${pieceAt(piece)} — кусок ${piece.index}/${piece.count} фразы, и её перевод ` +
        `${relation}: ${quotePieceTranslation(piece, en)}.\n\n` +
        `Это настоящий повтор в дубляже: фраза звучит ${timesWord(times, en)}. ${fix}\n\nУдалить?`
    );
};

/** Строка куска в списке: номер, повтор ли и что он повторяет, его перевод. */
const pieceListLine = (piece: PhrasePieceRef, en: boolean): string => {
    const repeat = isRepeatPiece(piece)
        ? en
            ? `, repeats the translation of ${ofPieces(piece.repeatsOf || [], piece.count, en)}${piece.repeatsWhole ? " together" : ""}`
            : `, повтор перевода ${ofPieces(piece.repeatsOf || [], piece.count, en)}${piece.repeatsWhole ? " вместе" : ""}`
        : "";
    return en
        ? `• ${piece.id}${pieceAt(piece)} — piece ${piece.index}/${piece.count}${repeat}: ${quotePieceTranslation(piece, en)}`
        : `• ${piece.id}${pieceAt(piece)} — кусок ${piece.index}/${piece.count}${repeat}: ${quotePieceTranslation(piece, en)}`;
};

const pieceList = (pieces: PhrasePieceRef[], en: boolean): string => {
    const shown = pieces.slice(0, 5).map((piece) => pieceListLine(piece, en));
    const more = pieces.length > 5 ? (en ? `\n… and ${pieces.length - 5} more` : `\n… и ещё ${pieces.length - 5}`) : "";
    return `${shown.join("\n")}${more}`;
};

/** Фразы, выбранные целиком: все куски фразы (по номерам) среди удаляемых. */
const wholePhrasesAmong = (pieces: PhrasePieceRef[]): PhrasePieceRef[][] => {
    const byParent = new Map<string, PhrasePieceRef[]>();
    pieces.forEach((piece) => {
        if (!piece.parent) return;
        const list = byParent.get(piece.parent);
        if (list) list.push(piece);
        else byParent.set(piece.parent, [piece]);
    });
    return Array.from(byParent.values()).filter(
        (list) => new Set(list.map((piece) => piece.index)).size >= list[0].count,
    );
};

/**
 * Вопрос перед удалением, когда среди удаляемых есть куски фраз. `total` — сколько реплик
 * выбрано всего (куски и обычные). Склейку кусков вопрос не советует: сервер склейки пока
 * ставит общую русскую фразу кусков подряд — «дублируется» руками самого редактора.
 */
export function phrasePieceDeleteQuestion(pieces: PhrasePieceRef[], total: number, en: boolean): string {
    if (pieces.length === 1 && total <= 1) {
        const [piece] = pieces;
        if (isRepeatPiece(piece)) return repeatPieceDeleteQuestion(piece, en);
        const repeaters = piece.groupRepeaters || [];
        if (en) {
            const shared =
                piece.sharesText === false
                    ? "The pieces have their own Russian text, translation and voicing."
                    : "The pieces share the Russian text, but each has its own part of the translation and its own voicing.";
            const note = repeaters.length
                ? `\n\nBut this phrase has a real repeat of the translation — in ${ofPieces(repeaters, piece.count, en)}: ` +
                  `delete ${repeaters.length > 1 ? "the extra one among them" : "that one"}, not this piece.`
                : "";
            return (
                `Cue ${piece.id}${pieceAt(piece)} is piece ${piece.index}/${piece.count} of one phrase, not a duplicate.\n\n` +
                `${shared} This piece has: ${quotePieceTranslation(piece, en)}.\n\n` +
                `Deleting it removes this part of the translation and its sound from the dub. ` +
                `To keep one cue per phrase, first move this piece's text into a neighbouring piece.${note}\n\nDelete?`
            );
        }
        const shared =
            piece.sharesText === false
                ? "У кусков свой русский текст, свой перевод и своя озвучка."
                : "Русский текст у кусков общий, но перевод и озвучка у каждого свои.";
        const note = repeaters.length
            ? `\n\nНо в этой фразе есть настоящий повтор перевода — у ${ofPieces(repeaters, piece.count, en)}: ` +
              `удалять стоит ${repeaters.length > 1 ? "лишний из них" : "его"}, а не этот кусок.`
            : "";
        return (
            `Реплика ${piece.id}${pieceAt(piece)} — кусок ${piece.index}/${piece.count} одной фразы, а не дубль.\n\n` +
            `${shared} У этого куска: ${quotePieceTranslation(piece, en)}.\n\n` +
            `Удаление уберёт эту часть перевода и её звук из дубляжа. ` +
            `Чтобы оставить одну реплику на фразу, сначала перенесите текст этого куска в соседний кусок.${note}\n\nУдалить?`
        );
    }
    const legit = pieces.filter((piece) => !isRepeatPiece(piece));
    const repeats = pieces.filter(isRepeatPiece);
    const wholePhrases = wholePhrasesAmong(pieces);
    const wholeParents = new Set(wholePhrases.map((list) => list[0].parent));
    const partlyDeleted = legit.some((piece) => !wholeParents.has(piece.parent));
    const parts: string[] = [en ? `Delete the selected cues (${total})?` : `Удалить выбранные реплики (${total})?`];
    const about: string[] = [];
    if (legit.length) {
        about.push(
            en
                ? `Among them are phrase pieces (${legit.length}), not duplicates: the pieces share the Russian text, ` +
                      `but each has its own part of the translation and its own voicing.`
                : `Среди них куски фраз (${legit.length}), а не дубли: русский текст у кусков общий, ` +
                      `но перевод и озвучка у каждого свои.`,
        );
    }
    if (repeats.length) {
        about.push(
            en
                ? `Among them are phrase pieces (${repeats.length}) whose translation repeats another piece of the same ` +
                      `phrase — real repeats in the dub: deleting them is the fix.`
                : `Среди них куски фраз (${repeats.length}), чей перевод повторяет перевод другого куска той же фразы, — ` +
                      `это настоящие повторы в дубляже: их удаление и есть исправление.`,
        );
    }
    parts.push(`${about.join(" ")}\n${pieceList(pieces, en)}`);
    wholePhrases.forEach((list) => {
        const count = list[0].count;
        const starts = list.map((piece) => piece.startMs).filter((ms): ms is number => typeof ms === "number");
        const from = starts.length ? formatCueTime(Math.min(...starts)) : "?";
        parts.push(
            en
                ? `All pieces of a phrase are selected (${count} of ${count}, from ${from}): the whole phrase goes — ` +
                      `its Russian, its translation and its voicing.`
                : `Выбраны все куски фразы (${count} из ${count}, с ${from}): удалится вся фраза — ` +
                      `и русский текст, и перевод, и озвучка.`,
        );
    });
    if (partlyDeleted) {
        parts.push(
            en
                ? "Deleting removes these parts of the translation and their sound from the dub. " +
                      "To keep one cue per phrase, first move a piece's text into a neighbouring piece."
                : "Удаление уберёт эти части перевода и их звук из дубляжа. " +
                      "Чтобы оставить одну реплику на фразу, сначала перенесите текст куска в соседний кусок.",
        );
    }
    return parts.join("\n\n");
}

/** В какой колонке склеивают: от этого зависит, что сервер сделает с озвучкой и русским текстом. */
export type PhrasePieceMergeColumn = "source" | "translation";

/**
 * Вопрос перед склейкой двух реплик, когда хотя бы одна — кусок фразы; null — ни одна не кусок,
 * спрашивать не о чем. Что делает сервер /fast (translate-python deploy-de-llm-stt):
 * склейка колонки оригинала сводит озвучки частей в одну, а русские тексты ставит подряд
 * (stt.py merge_json_files); склейка колонки перевода тоже ставит русские подряд
 * (split_translated.py joined_ru) и озвучивает склеенный перевод заново одним дублем.
 */
export function phrasePieceMergeQuestion(
    a: PhrasePieceRef | null,
    b: PhrasePieceRef | null,
    en: boolean,
    column: PhrasePieceMergeColumn = "source",
): string | null {
    if (!a && !b) return null;
    const voicedAnew = en
        ? "The merged translation will be voiced anew, as one new take in place of the pieces' voicings."
        : "Склеенный перевод будет озвучен заново — одним новым дублем вместо озвучек кусков.";
    if (a && b && a.parent && a.parent === b.parent) {
        const [first, second] = a.index <= b.index ? [a, b] : [b, a];
        const head = en
            ? `Merge pieces ${first.index}/${first.count} and ${second.index}/${second.count} of one phrase?`
            : `Склеить куски ${first.index}/${first.count} и ${second.index}/${second.count} одной фразы?`;
        const quotes = en
            ? `${quotePieceTranslation(first, en)} and ${quotePieceTranslation(second, en)}`
            : `${quotePieceTranslation(first, en)} и ${quotePieceTranslation(second, en)}`;
        const repeat =
            (first.repeatsOf || []).indexOf(second.index) >= 0 || (second.repeatsOf || []).indexOf(first.index) >= 0;
        if (repeat) {
            return en
                ? `${head}\n\nTheir translations repeat each other — ${quotes}: this is a real repeat in the dub, and ` +
                      `merging would join the same phrase twice. To remove the repeat, delete the extra piece instead of merging.` +
                      `\n\nMerge anyway?`
                : `${head}\n\nПеревод этих кусков повторяется — ${quotes}: это настоящий повтор в дубляже, и склейка ` +
                      `соединит одну и ту же фразу дважды. Чтобы убрать повтор, удалите лишний кусок, а не склеивайте.` +
                      `\n\nСклеить всё равно?`;
        }
        const firstRu = pieceText(first.captions);
        const sameRussian = firstRu !== "" && firstRu === pieceText(second.captions);
        let russianNote = "";
        if (sameRussian) {
            // Колонка оригинала: исправленный сервер пишет общую фразу один раз, нынешний — подряд;
            // поэтому «может» и «проверьте». Колонка перевода повторяет её всегда.
            russianNote =
                column === "translation"
                    ? en
                        ? "\n\nThe pieces have the same Russian text — the whole phrase — and merging puts the parts' Russian " +
                          "in a row: the phrase will be repeated twice in the merged cue — leave it once."
                        : "\n\nРусский текст у кусков один и тот же — вся фраза, а склейка ставит русские тексты частей " +
                          "подряд: в склеенной реплике фраза повторится дважды — оставьте её один раз."
                    : en
                      ? "\n\nThe pieces have the same Russian text — the whole phrase — and merging may put it twice in " +
                        "a row: check the merged cue's Russian and leave the phrase once."
                      : "\n\nРусский текст у кусков один и тот же — вся фраза, и склейка может поставить её подряд " +
                        "дважды: проверьте русский текст склеенной реплики и оставьте фразу один раз.";
        }
        const result =
            column === "translation"
                ? en
                    ? `The parts of the translation will be joined in order. ${voicedAnew}`
                    : `Переводы частей встанут подряд. ${voicedAnew}`
                : en
                  ? "The parts of the translation will be joined in order, and the voicings combined into one."
                  : "Переводы частей встанут подряд, озвучки сведутся в одну.";
        return en
            ? `${head}\n\nThey are not duplicates: each piece has its own part of the translation and its own voicing — ` +
                  `${quotes}. ${result}${russianNote}`
            : `${head}\n\nЭто не дубли: перевод и озвучка у каждого куска свои — ${quotes}. ${result}${russianNote}`;
    }
    const lines = [a, b]
        .filter((piece): piece is PhrasePieceRef => Boolean(piece))
        .map((piece) => {
            if (isRepeatPiece(piece)) {
                const relation = phrasePieceRepeatRelation(piece.repeatsOf || [], Boolean(piece.repeatsWhole), piece.count, en);
                return en
                    ? `Cue ${piece.id}${pieceAt(piece)} is piece ${piece.index}/${piece.count} of a phrase, and its translation ` +
                          `${relation}: ${quotePieceTranslation(piece, en)} — a real repeat in the dub; to remove it, delete ` +
                          `the piece rather than merge it.`
                    : `Реплика ${piece.id}${pieceAt(piece)} — кусок ${piece.index}/${piece.count} фразы, и её перевод ` +
                          `${relation}: ${quotePieceTranslation(piece, en)} — это настоящий повтор в дубляже; чтобы его ` +
                          `убрать, кусок удаляют, а не склеивают.`;
            }
            return en
                ? `Cue ${piece.id}${pieceAt(piece)} is piece ${piece.index}/${piece.count} of one phrase, not a duplicate: ` +
                      `it has its own part of the translation, ${quotePieceTranslation(piece, en)}, and its own voicing.`
                : `Реплика ${piece.id}${pieceAt(piece)} — кусок ${piece.index}/${piece.count} одной фразы, а не дубль: ` +
                      `у неё своя часть перевода ${quotePieceTranslation(piece, en)} и своя озвучка.`;
        });
    if (column === "translation") lines.push(voicedAnew);
    return `${lines.join("\n\n")}\n\n${en ? "Merge?" : "Склеить?"}`;
}

/**
 * Предупреждение «→» (машинный перевод) у куска, чей русский текст — общая фраза кусков: перевод
 * ВСЕЙ фразы ляжет в этот кусок, а у других кусков свои части — фраза прозвучит дважды (так 01.10
 * был перезаписан «Pienso:»). null — не кусок, или у куска свой русский текст.
 */
export function phrasePieceTranslateWarning(
    track: Pick<PhrasePieceRow, "piece_index" | "piece_count" | "piece_shares_text" | "captions">,
    en: boolean,
): string | null {
    if (!track.piece_index || !track.piece_count || track.piece_count < 2 || !track.piece_shares_text) return null;
    const russian = (track.captions || "").trim();
    const shown = russian.length > 200 ? `${russian.slice(0, 200)}…` : russian;
    return en
        ? `This cue is piece ${track.piece_index}/${track.piece_count} of one phrase, and its Russian text is the whole ` +
              `phrase, shared with the other pieces: «${shown}».\n\nMachine translation would translate the WHOLE phrase ` +
              `into this piece while the other pieces keep their own parts — the phrase would be heard twice in the dub. ` +
              `Better translate this piece by hand, with its own part only.`
        : `Реплика — кусок ${track.piece_index}/${track.piece_count} одной фразы, и её русский текст — вся фраза, общая ` +
              `с другими кусками: «${shown}».\n\nМашинный перевод переведёт ВСЮ фразу и запишет её в этот кусок, а у ` +
              `других кусков свои части перевода — в дубляже фраза прозвучит дважды. Лучше перевести этот кусок вручную, ` +
              `только его частью.`;
}

/**
 * Пометка к вопросу «Заменить промежуток новой расшифровкой» (он удаляет старые реплики без
 * второго вопроса): какие из них — куски фраз, с их переводом. null — кусков среди них нет.
 */
export function phrasePieceReplaceNote(rows: AudioTrack[] | undefined, ids: string[], en: boolean): string | null {
    const pieces = ids
        .map((id) => phrasePieceOf(rows, id))
        .filter((piece): piece is PhrasePieceRef => Boolean(piece));
    if (!pieces.length) return null;
    return en
        ? `Among them are phrase pieces (${pieces.length}) — the replacement removes their translation and voicing too:\n` +
              pieceList(pieces, en)
        : `Среди них куски фраз (${pieces.length}) — замена удалит и их перевод с озвучкой:\n${pieceList(pieces, en)}`;
}

const calcEndRusMilli = (track: AudioTrackData) => {
    if (track.length) {
        return track.start + track.length;
    }
    if (track.end_rus_milli) {
        return track.end_rus_milli;
    }
    return convertTime(track.end_rus || "");
};

export const getFolderStartTime = (dataAll: AudioFolderData[], folder: number) => {
    if (folder < dataAll.length) {
        const folderData = dataAll[folder];
        if (folderData && folderData.translations.length) {
            return folderData.translations[0]["start"];
        }
    }
    return null;
};

export function getFromLocal(mediaId: undefined, arg: ChangedDataKeys, folder?: number): never[];
export function getFromLocal(mediaId: string | undefined, arg: ChangedDataKeys.play, folder?: number): string;
export function getFromLocal(
    mediaId: string,
    arg: Exclude<ChangedDataKeys, ChangedDataKeys.play>,
    folder?: number,
): string[];
export function getFromLocal(mediaId: string | undefined, what: ChangedDataKeys, folder?: number) {
    if (!mediaId) {
        return [];
    }
    const key = folder ? `${mediaId}_${folder}` : mediaId;
    let data: IChangedData = {
        play: "",
        changed: [],
        changedTranslation: [],
        changedRus: [],
        changedReels: [],
    };

    const localData = localStorage.getItem(key);
    if (localData) {
        const parsedData = tryParse<IChangedData>(localData);
        if (parsedData) {
            data = parsedData;
        }
    }
    if (what === ChangedDataKeys.play) {
        return data[what];
    }
    return data[what] ? data[what] : [];
}

export const getCaptionSizeFromLocal = (lang: ILang) => {
    const size = localStorage.getItem(LocalStorageKeys.captionSize);
    if (size) {
        const data = tryParse<ICaptionSizeState>(size);
        if (data) {
            return data;
        }
    }
    return {
        ru: 12,
        [lang]: 12,
    };
};

export const getPlayerFromLocal = () => {
    let d;
    d = localStorage.getItem("playerType");
    // return d ? d : 'advanced';
    return d ? d : "simple";
};

const jsonPreview = (jsonString: string) => {
    const newWindow = window.open();
    newWindow?.document.write(`<pre>${jsonString}</pre>`);
};

function formatMilliseconds(ms: number): string {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    const milliseconds = ms % 1000;

    const pad = (num: number, size: number = 2) => {
        return num.toString().padStart(size, "0");
    };

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}.${pad(milliseconds, 3)}`;
}

export function patchTrackInSubtitlesData(
    subtitlesDataAll: Partial<ISubtitlesDataAll>,
    lang: ILang,
    folder: number,  // 1-based
    trackId: string, // original_id
    patch: Partial<AudioTrackData>
): Partial<ISubtitlesDataAll> {
    const langData = subtitlesDataAll[lang];
    if (!langData) return subtitlesDataAll;
    const folderIndex = folder - 1;
    const folderData = langData[folderIndex];
    if (!folderData) return subtitlesDataAll;
    const newTranslations = folderData.translations.map(t =>
        t.id === trackId ? { ...t, ...patch } : t
    );
    const newFolderData = { ...folderData, translations: newTranslations };
    const newLangData = [...langData];
    newLangData[folderIndex] = newFolderData;
    return { ...subtitlesDataAll, [lang]: newLangData };
}

export function patchMultipleTracksInSubtitlesData(
    subtitlesDataAll: Partial<ISubtitlesDataAll>,
    lang: ILang,
    folder: number,  // 1-based
    patches: Array<{ trackId: string; patch: Partial<AudioTrackData> }>
): Partial<ISubtitlesDataAll> {
    const langData = subtitlesDataAll[lang];
    if (!langData) return subtitlesDataAll;
    const folderIndex = folder - 1;
    const folderData = langData[folderIndex];
    if (!folderData) return subtitlesDataAll;
    const patchMap = new Map(patches.map(p => [p.trackId, p.patch]));
    const newTranslations = folderData.translations.map(t => {
        const p = patchMap.get(t.id);
        return p ? { ...t, ...p } : t;
    });
    const newFolderData = { ...folderData, translations: newTranslations };
    const newLangData = [...langData];
    newLangData[folderIndex] = newFolderData;
    return { ...subtitlesDataAll, [lang]: newLangData };
}

/**
 * Добавить в папку реплики, которых в ней ещё нет (сервер только что создал их файл — например,
 * перевод новой реплики из первого сохранённого текста). Уже известные не трогает.
 */
export function addMissingTracksToSubtitlesData(
    subtitlesDataAll: Partial<ISubtitlesDataAll>,
    lang: ILang,
    folder: number,  // 1-based
    tracks: AudioTrackData[]
): Partial<ISubtitlesDataAll> {
    const langData = subtitlesDataAll[lang];
    if (!langData) return subtitlesDataAll;
    const folderIndex = folder - 1;
    const folderData = langData[folderIndex];
    if (!folderData) return subtitlesDataAll;
    const known = new Set(folderData.translations.map(t => t.id));
    const added = tracks.filter(t => t && t.id && !known.has(t.id));
    if (!added.length) return subtitlesDataAll;
    const newFolderData = { ...folderData, translations: [...folderData.translations, ...added] };
    const newLangData = [...langData];
    newLangData[folderIndex] = newFolderData;
    return { ...subtitlesDataAll, [lang]: newLangData };
}

export const getSpaces = ({
    tracks,
    voicingSpacesMinimumOffset,
    voicingSpacesMinimum,
    nextFolderFirstTrackStart = 0,
    durationFull,
    prevFolderSpaceLength,
}: {
    tracks: AudioTrackData[];
    voicingSpacesMinimumOffset: number;
    voicingSpacesMinimum: number;
    durationFull?: number;
    nextFolderFirstTrackStart?: number;
    prevFolderSpaceLength?: number;
}) => {
    // Паузы озвучки — между звучащими дублями: место снятой с озвучки (⊘) реплики — тоже пауза.
    const spaces = tracks
        .filter(voicingSounds)
        .map((track, index, translationData) => {
            let previousTrackEnd;
            let nextTrackStart;

            if (index === 0) {
                previousTrackEnd = 0;
                nextTrackStart = Math.max(track.start - voicingSpacesMinimumOffset, 0);
            } else {
                previousTrackEnd =
                    translationData[index - 1].start +
                    translationData[index - 1].length +
                    voicingSpacesMinimumOffset;
                nextTrackStart = Math.max(track.start - voicingSpacesMinimumOffset, 0);
            }

            return {
                start: previousTrackEnd,
                end: nextTrackStart,
                nextTrackId: index < translationData.length ? track.id : undefined, // ID of the current track
            } as ISpace;
        });
    if (tracks.length) {
        let lastTrack = tracks.findLast(voicingSounds);
        if (lastTrack) {
            const lastSpace = {
                start: lastTrack.start + lastTrack.length + voicingSpacesMinimumOffset,
                end: durationFull,
                nextTrackId: undefined,
            };
            spaces.push(lastSpace);
        }
    }

    return spaces.filter((space, index, arr) => {
        if (index === 0 && prevFolderSpaceLength && space.end - space.start > 0) {
            return space.end - space.start + prevFolderSpaceLength >= voicingSpacesMinimum;
        }
        if (index === arr.length - 1) {
            return space.end - space.start + nextFolderFirstTrackStart >= voicingSpacesMinimum;
        }
        return space.end - space.start >= voicingSpacesMinimum;
    });
};

export const getSpacesForAllPages = ({
    tracksAll,
    voicingSpacesMinimumOffset,
    voicingSpacesMinimum,
}: {
    tracksAll: AudioFolderData[];
    voicingSpacesMinimumOffset: number;
    voicingSpacesMinimum: number;
}) => {
    const voisingTracks = tracksAll.reduce((acc, tracks, index, arr) => {
        const durationFull =
            index === arr.length - 1
                ? 0
                : parseInt(arr[index + 1].prev_mp3_length_aggr) -
                  parseInt(arr[index].prev_mp3_length_aggr);
        const prevTrack = index === 0 ? null : arr[index - 1];
        let prevFolderSpaceLength = 0;
        if (prevTrack) {
            const prevDuration =
                parseInt(tracks.prev_mp3_length_aggr) - parseInt(prevTrack?.prev_mp3_length_aggr);
            prevFolderSpaceLength =
                prevDuration -
                    (prevTrack?.translations[prevTrack.translations.length - 1]?.start +
                        prevTrack?.translations[prevTrack.translations.length - 1]?.length) ||
                prevDuration;
        }

        return {
            ...acc,
            [tracks.folder_num]: getSpaces({
                tracks: tracks.translations,
                voicingSpacesMinimumOffset,
                voicingSpacesMinimum,
                nextFolderFirstTrackStart: arr[index + 1]?.translations[0]?.start || 0,
                durationFull,
                prevFolderSpaceLength: prevFolderSpaceLength,
            }),
        };
    }, {});
    return voisingTracks;
};
